import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, InputGroup, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import { api } from '../../lib/api';
import { buscarEnderecoPorCep } from '../../lib/cep';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import GaleriaImovel from './GaleriaImovel';
import DocumentosImovel from './DocumentosImovel';
import GerarAnuncioIa from './GerarAnuncioIa';
import MapaImovel from './MapaImovel';
import SeletorProprietario from './SeletorProprietario';
import { campoParaNumero, formatarMoeda, mascararDinheiro, textoDatasImovel } from './formatos';
import { geocodificar } from '../../lib/geocodificacao';
import ModalTrocarTipo from './ModalTrocarTipo';
import {
  CAMPOS_PUBLICAR, CATEGORIAS, ESTADOS_ATUAIS, ESTADOS_BR, FACES, FINALIDADES, FINANCIAMENTOS,
  ISOLAMENTOS, LOCALIZACOES, MEDIDAS, OCUPACOES, PADROES, PGTO_IPTU, POSICOES, SITUACOES,
} from './constantes';

// `v`: valores do formulário (texto/booleano). `setV(nome, valor)`. Todo o estado
// mora em PaginaImovel (que salva); aqui só a apresentação das 7 abas do legado.
export default function FormImovel({
  ehNovo, v, setV, imovel, proprietario, setProprietario, captador, setCaptador,
  tipos, captadores, caracteristicas, setCaracteristicas, fotos, setFotos, pendentes, setPendentes,
  empresaId, podeEditar, salvando, aoSalvar, erroCampos, temAlteracoes, aoTrocarTipo, aoCopiarImovel,
}) {
  const toast = useToast();
  const { usuario } = useAuth();
  const podeUsarIa = usuario?.permissoes?.ia_anuncio === true;
  const [aba, setAba] = useState('principal');
  const [modalTipo, setModalTipo] = useState(false);
  const [gruposCarac, setGruposCarac] = useState(null);
  const [escolhendoProprietario, setEscolhendoProprietario] = useState(false);
  const [mostrarIa, setMostrarIa] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [buscandoMapa, setBuscandoMapa] = useState(false);
  const enderecoInicial = useRef(null);
  const sequenciaMapa = useRef(0);

  // características que o TIPO permite (troca de tipo só é possível no cadastro novo)
  useEffect(() => {
    if (!v.i1_tipo) {
      setGruposCarac([]);
      return undefined;
    }
    let cancelado = false;
    setGruposCarac(null);
    api
      .get(`/tipos/${v.i1_tipo}/caracteristicas`)
      .then((r) => !cancelado && setGruposCarac(r.data))
      .catch((err) => {
        if (!cancelado) {
          setGruposCarac([]);
          toast.erro(err.message || 'Não foi possível carregar as características');
        }
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.i1_tipo]);

  const precoM2 = useMemo(() => {
    const venda = campoParaNumero(v.i1_vlrvenda);
    const construida = Number(String(v.i1_construida || '').replace(',', '.'));
    return venda > 0 && construida > 0 ? venda / construida : null;
  }, [v.i1_vlrvenda, v.i1_construida]);

  const faltantes = useMemo(
    () => CAMPOS_PUBLICAR.filter(([campo]) => (campo === 'proprietario' ? !proprietario : !String(v[campo] ?? '').trim() || String(v[campo]).trim() === '0')),
    [v, proprietario]
  );

  // ─── helpers de campo (funções, não componentes: não remontam a cada tecla) ───
  const desabilitado = !podeEditar;

  const texto = (nome, rotulo, md = 4, extra = {}) => (
    <Col md={md} key={nome}>
      <Form.Group controlId={nome}>
        <Form.Label>{rotulo}</Form.Label>
        <Form.Control value={v[nome] ?? ''} disabled={desabilitado} onChange={(e) => setV(nome, e.target.value)} autoComplete="off" {...extra} />
      </Form.Group>
    </Col>
  );

  const numero = (nome, rotulo, md = 2, passo = '1') => (
    <Col md={md} sm={6} xs={6} key={nome}>
      <Form.Group controlId={nome}>
        <Form.Label>{rotulo}</Form.Label>
        <Form.Control type="number" min="0" step={passo} value={v[nome] ?? ''} disabled={desabilitado} onChange={(e) => setV(nome, e.target.value)} />
      </Form.Group>
    </Col>
  );

  const dinheiro = (nome, rotulo, md = 3) => (
    <Col md={md} sm={6} key={nome}>
      <Form.Group controlId={nome}>
        <Form.Label>{rotulo}</Form.Label>
        <InputGroup>
          <InputGroup.Text>R$</InputGroup.Text>
          <Form.Control inputMode="numeric" value={v[nome] ?? ''} disabled={desabilitado} onChange={(e) => setV(nome, mascararDinheiro(e.target.value))} autoComplete="off" />
        </InputGroup>
      </Form.Group>
    </Col>
  );

  const selecao = (nome, rotulo, opcoes, md = 3, desativado = false) => {
    const atual = v[nome] ?? '';
    // valor legado fora da lista (ex.: "Vendido") continua visível em vez de sumir
    const lista = atual && !opcoes.includes(atual) ? [...opcoes, atual] : opcoes;
    return (
      <Col md={md} sm={6} key={nome}>
        <Form.Group controlId={nome}>
          <Form.Label>{rotulo}</Form.Label>
          <Form.Select value={atual} disabled={desabilitado || desativado} onChange={(e) => setV(nome, e.target.value)}>
            {lista.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>
    );
  };

  const check = (nome, rotulo, md = 4) => (
    <Col md={md} sm={6} key={nome}>
      <Form.Check type="checkbox" id={nome} label={rotulo} checked={v[nome] === true} disabled={desabilitado} onChange={(e) => setV(nome, e.target.checked)} />
    </Col>
  );

  // ─── ações da aba Localização ───
  async function aoSairDoCep() {
    setBuscandoCep(true);
    const achado = await buscarEnderecoPorCep(v.i1_cep);
    setBuscandoCep(false);
    if (!achado) return;
    setV('i1_endereco', achado.endereco);
    setV('i1_bairroofi', achado.bairro);
    setV('i1_cidade', achado.cidade);
    setV('i1_estado', achado.estado);
    // o mapa acompanha sozinho: ver o efeito de endereço abaixo
  }

  // Botão "Localizar pelo endereço" (o mesmo que o efeito automático faz, mas avisando se não achar)
  async function buscarCoordenadas() {
    setBuscandoMapa(true);
    const achado = await geocodificar({ endereco: v.i1_endereco, numero: v.i1_numero, bairro: v.i1_bairroofi, cidade: v.i1_cidade, estado: v.i1_estado, cep: v.i1_cep });
    setBuscandoMapa(false);
    if (achado) {
      setV('i1_latitude', String(achado.lat));
      setV('i1_longitude', String(achado.lng));
    } else {
      toast.erro('Não encontrei este endereço no mapa. Clique no mapa para marcar o ponto.');
    }
  }

  // Pedido de Jason (2026-09-25): conforme o endereço muda, latitude/longitude e o mapa
  // acompanham. Espera o usuário parar de digitar (1,2 s) antes de consultar; resposta de
  // consulta antiga é descartada; não roda ao abrir um imóvel já salvo (só em MUDANÇA).
  const chaveEndereco = [v.i1_cep, v.i1_endereco, v.i1_numero, v.i1_bairroofi, v.i1_cidade, v.i1_estado].join('|');
  useEffect(() => {
    if (enderecoInicial.current === null) {
      enderecoInicial.current = chaveEndereco;
      return undefined;
    }
    if (!podeEditar || !v.i1_cidade || !(v.i1_endereco || v.i1_cep)) return undefined;
    const minha = ++sequenciaMapa.current;
    const espera = setTimeout(async () => {
      setBuscandoMapa(true);
      const achado = await geocodificar({ endereco: v.i1_endereco, numero: v.i1_numero, bairro: v.i1_bairroofi, cidade: v.i1_cidade, estado: v.i1_estado, cep: v.i1_cep });
      if (minha !== sequenciaMapa.current) return; // o endereço mudou de novo: vale a consulta mais nova
      setBuscandoMapa(false);
      if (achado) {
        setV('i1_latitude', String(achado.lat));
        setV('i1_longitude', String(achado.lng));
      }
    }, 1200);
    return () => clearTimeout(espera);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveEndereco]);

  const temCoordenadas = Number.isFinite(parseFloat(v.i1_latitude)) && Number.isFinite(parseFloat(v.i1_longitude));

  function alternarCaracteristica(id, marcada) {
    const novo = new Set(caracteristicas);
    if (marcada) novo.add(id);
    else novo.delete(id);
    setCaracteristicas(novo);
  }

  function aoEnviar(e) {
    e.preventDefault();
    aoSalvar();
  }

  const situacaoBadge = imovel
    ? imovel.i1_rascunho === 'true'
      ? { bg: 'secondary', rotulo: 'Cadastro incompleto' }
      : imovel.i1_situacao === 'Ativo'
        ? { bg: 'success', rotulo: 'Ativo' }
        : { bg: 'danger', rotulo: imovel.i1_situacao || 'Inativo' }
    : null;

  // Atributos do imóvel (sem valor/preço, de propósito) para a IA escrever o anúncio — sempre a
  // partir do que já está no formulário, mesmo sem ter sido salvo ainda.
  function montarDadosParaIa() {
    const modalidades = [];
    if (campoParaNumero(v.i1_vlrvenda) > 0) modalidades.push('Venda');
    if (campoParaNumero(v.i1_locacao) > 0) modalidades.push('Locação');
    if (campoParaNumero(v.i1_temporada) > 0) modalidades.push('Temporada');
    const caracteristicasNomes = (gruposCarac || [])
      .flatMap((grupo) => grupo.itens)
      .filter((item) => caracteristicas.has(item.id))
      .map((item) => item.descricao);
    return {
      tipo: tipos.find((t) => String(t.id) === String(v.i1_tipo))?.descricao || '',
      categoria: v.i1_categoria || '',
      finalidade: v.i1_finalidade || '',
      modalidades,
      dormitorios: v.i1_qtddormitorios || '',
      suites: v.i1_qtdsuites || '',
      banheiros: v.i1_qtdbanheiros || '',
      garagens: v.i1_qtdgaragens || '',
      areaConstruida: v.i1_construida || '',
      areaTerreno: v.i1_totalterreno || '',
      bairro: v.i1_bairroofi || '',
      cidade: v.i1_cidade || '',
      estado: v.i1_estado || '',
      condominio: v.i1_possuicondominio === true ? v.i1_condominio || '' : '',
      caracteristicas: caracteristicasNomes,
    };
  }

  const conteudoAnuncioJaExiste = Boolean(
    (v.i1_seotitulo || '').trim() || (v.i1_descricao || '').trim() || (v.i1_seodescricao || '').trim()
  );

  function aoAceitarIa({ titulo, descricao, seoDescricao, palavrasChave }) {
    setV('i1_seotitulo', titulo);
    setV('i1_descricao', descricao);
    setV('i1_seodescricao', seoDescricao);
    setV('i1_seoplvrchave', palavrasChave);
  }

  return (
    <Form onSubmit={aoEnviar}>
      {!ehNovo && imovel && (
        <div className="mb-3">
          {situacaoBadge && <Badge bg={situacaoBadge.bg} className="me-2">{situacaoBadge.rotulo}</Badge>}
          <strong>{imovel.i1_codsequencia}</strong>
          {imovel.i1_seotitulo ? <span> — {imovel.i1_seotitulo}</span> : null}
        </div>
      )}
      {!podeEditar && !ehNovo && (
        <Alert variant="info">Você pode visualizar este imóvel, mas só quem o cadastrou (ou o administrador) pode alterá-lo.</Alert>
      )}
      {erroCampos && <Alert variant="danger">{erroCampos}</Alert>}

      <Tabs id="abas-imovel" activeKey={aba} onSelect={(k) => setAba(k)} className="mb-3">
        {/* ───────── Principal ───────── */}
        <Tab eventKey="principal" title="Principal">
          <Card className="mb-3">
            <Card.Header>Cadastro</Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={3} sm={6}>
                  <Form.Group controlId="i1_tipo">
                    <Form.Label>Tipo</Form.Label>
                    <Form.Select value={v.i1_tipo ?? ''} disabled={!ehNovo || desabilitado} onChange={(e) => { setV('i1_tipo', e.target.value); setCaracteristicas(new Set()); }} required>
                      {ehNovo && <option value="">Selecione...</option>}
                      {tipos.map((t) => (
                        <option key={t.id} value={String(t.id)}>
                          {t.descricao}
                        </option>
                      ))}
                    </Form.Select>
                    {!ehNovo && (
                      <Form.Text muted>
                        {podeEditar ? (
                          <>
                            <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => setModalTipo(true)} disabled={temAlteracoes}>
                              Trocar tipo
                            </Button>
                            {temAlteracoes ? ' (salve as alterações antes)' : ''}
                          </>
                        ) : 'O tipo só pode ser trocado por quem pode editar o imóvel.'}
                      </Form.Text>
                    )}
                    {!ehNovo && imovel && (
                      <ModalTrocarTipo
                        show={modalTipo}
                        aoFechar={() => setModalTipo(false)}
                        publicId={imovel.public_id}
                        tipos={tipos}
                        tipoAtual={v.i1_tipo}
                        aoTrocar={aoTrocarTipo}
                        trocaTipo={imovel.troca_tipo}
                        aoCopiar={aoCopiarImovel}
                      />
                    )}
                  </Form.Group>
                </Col>
                {selecao('i1_categoria', 'Categoria', CATEGORIAS)}
                {selecao('i1_finalidade', 'Finalidade', FINALIDADES)}
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>Valores</Card.Header>
            <Card.Body>
              <Row className="g-3">
                {dinheiro('i1_vlrvenda', 'Venda')}
                <Col md={3} sm={6}>
                  <Form.Group controlId="precom2">
                    <Form.Label>Preço por m²</Form.Label>
                    <Form.Control readOnly value={precoM2 ? formatarMoeda(precoM2) : ''} placeholder="calculado (venda ÷ área construída)" />
                  </Form.Group>
                </Col>
                {dinheiro('i1_locacao', 'Locação')}
                {dinheiro('i1_temporada', 'Temporada')}
                {dinheiro('i1_estudante', 'Estudante')}
                {dinheiro('i1_vlrcondominio', 'Valor do condomínio')}
                {dinheiro('i1_vlriptu', 'Valor do IPTU')}
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>Situação</Card.Header>
            <Card.Body>
              <Row className="g-3">
                {selecao('i1_financiamento', 'Financiamento', FINANCIAMENTOS, 2)}
                {selecao('i1_pgtoiptu', 'Pagamento do IPTU', PGTO_IPTU, 2)}
                {selecao('i1_situacao', 'Situação', SITUACOES, 2)}
                {selecao('i1_estadoatual', 'Estado', ESTADOS_ATUAIS, 2)}
                {selecao('i1_ocupacao', 'Ocupação', OCUPACOES, 2)}
                {selecao('i1_padrao', 'Padrão', PADROES, 2)}
                {selecao('i1_localizacao', 'Localização', LOCALIZACOES, 2)}
                {texto('i1_referencia', 'Referência auxiliar', 3)}
                {check('i1_financiado', 'Financiado', 2)}
                {check('i1_permuta', 'Aceita permuta', 2)}
                <Col md={8}>
                  <Form.Group controlId="i1_descrinterna">
                    <Form.Label>Descrição interna (confidencial)</Form.Label>
                    <Form.Control as="textarea" rows={3} value={v.i1_descrinterna ?? ''} disabled={desabilitado} onChange={(e) => setV('i1_descrinterna', e.target.value)} />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {v.i1_permuta === true && (
            <Card className="mb-3">
              <Card.Header>Permuta (especificar valores)</Card.Header>
              <Card.Body>
                <Row className="g-3">
                  {dinheiro('i1_veiculopermuta', 'Aceita veículo até')}
                  {dinheiro('i1_imovelpermuta', 'Aceita imóvel até')}
                </Row>
              </Card.Body>
            </Card>
          )}
        </Tab>

        {/* ───────── Características ───────── */}
        <Tab eventKey="caracteristicas" title="Características">
          <Card className="mb-3">
            <Card.Header>Características</Card.Header>
            <Card.Body>
              {!v.i1_tipo && <p className="text-muted">Escolha o tipo do imóvel na aba Principal.</p>}
              {v.i1_tipo && !gruposCarac && <Spinner animation="border" size="sm" />}
              {gruposCarac && gruposCarac.length === 0 && v.i1_tipo && <p className="text-muted">Nenhuma característica cadastrada para este tipo.</p>}
              {gruposCarac?.map((grupo) => (
                <div key={grupo.nivel_id} className="mb-3">
                  <h6 className="border-bottom pb-1">{grupo.nivel}</h6>
                  <Row className="g-2">
                    {grupo.itens.map((item) => (
                      <Col xs={12} sm={6} md={4} lg={3} key={item.id}>
                        <Form.Check
                          type="checkbox"
                          id={`carac-${item.id}`}
                          label={item.descricao}
                          checked={caracteristicas.has(item.id)}
                          disabled={desabilitado}
                          onChange={(e) => alternarCaracteristica(item.id, e.target.checked)}
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Tab>

        {/* ───────── Localização ───────── */}
        <Tab eventKey="localizacao" title="Localização">
          <Card className="mb-3">
            <Card.Header>Localização</Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={2} sm={6}>
                  <Form.Group controlId="i1_cep">
                    <Form.Label>CEP {buscandoCep && <Spinner animation="border" size="sm" />}</Form.Label>
                    <Form.Control value={v.i1_cep ?? ''} disabled={desabilitado} onChange={(e) => setV('i1_cep', e.target.value)} onBlur={aoSairDoCep} autoComplete="off" />
                  </Form.Group>
                </Col>
                {texto('i1_endereco', 'Endereço', 4)}
                <Col md={2} sm={6}>
                  <Form.Group controlId="i1_numero">
                    <Form.Label>Número</Form.Label>
                    <Form.Control inputMode="numeric" value={v.i1_numero ?? ''} disabled={desabilitado} onChange={(e) => setV('i1_numero', e.target.value.replace(/\D/g, ''))} autoComplete="off" />
                  </Form.Group>
                </Col>
                {texto('i1_bairroofi', 'Bairro', 4)}
                {texto('i1_cidade', 'Cidade', 4)}
                <Col md={2} sm={6}>
                  <Form.Group controlId="i1_estado">
                    <Form.Label>Estado</Form.Label>
                    <Form.Select value={v.i1_estado ?? ''} disabled={desabilitado} onChange={(e) => setV('i1_estado', e.target.value)}>
                      {ESTADOS_BR.map(([sigla, nome]) => (
                        <option key={sigla} value={sigla}>
                          {nome}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                {texto('i1_complemento', 'Complemento', 4)}
                {texto('i1_quadra', 'Quadra', 2)}
                {texto('i1_lote', 'Lote', 2)}
                {check('i1_possuicondominio', 'Imóvel em condomínio?', 4)}
                {v.i1_possuicondominio === true && texto('i1_condominio', 'Nome do condomínio', 4, { placeholder: 'Informe o nome do condomínio' })}
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>Mapa</Card.Header>
            <Card.Body>
              <p className="text-muted small mb-2">O mapa acompanha o endereço automaticamente {buscandoMapa && <Spinner animation="border" size="sm" />}. Para ajustar o ponto exato, clique no mapa ou arraste o marcador.</p>
              <MapaImovel latitude={v.i1_latitude} longitude={v.i1_longitude} aoMudar={(lat, lng) => { setV('i1_latitude', lat); setV('i1_longitude', lng); }} />
              <Row className="g-3 mt-1 align-items-end">
                {texto('i1_latitude', 'Latitude', 3)}
                {texto('i1_longitude', 'Longitude', 3)}
                <Col md={6} className="d-flex flex-wrap gap-2">
                  <Button variant="outline-primary" disabled={desabilitado || buscandoMapa} onClick={buscarCoordenadas}>
                    {buscandoMapa ? <Spinner animation="border" size="sm" /> : 'Localizar pelo endereço'}
                  </Button>
                  <Button
                    variant="primary"
                    disabled={!temCoordenadas}
                    as="a"
                    target="_blank"
                    rel="noreferrer"
                    href={`https://www.google.com/maps/dir//${v.i1_latitude},${v.i1_longitude}/@${v.i1_latitude},${v.i1_longitude},17z?hl=pt-BR`}
                  >
                    Abrir no Google Maps
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        {/* ───────── Detalhes ───────── */}
        <Tab eventKey="detalhes" title="Detalhes">
          <Card className="mb-3">
            <Card.Header>Detalhes</Card.Header>
            <Card.Body>
              <Row className="g-3">
                {numero('i1_qtddormitorios', 'Dormitórios')}
                {numero('i1_qtdsuites', 'Suítes')}
                {numero('i1_qtdsuitescloset', 'Suítes c/ closet')}
                {numero('i1_qtddemisuites', 'Demi-suítes')}
                {numero('i1_qtdsalas', 'Salas')}
                {numero('i1_qtdsaladeestar', 'Sala de estar')}
                {numero('i1_qtdsaladejantar', 'Sala de jantar')}
                {numero('i1_qtdsaladetv', 'Sala de TV')}
                {numero('i1_qtdgaragens', 'Garagens')}
                {numero('i1_qtdgaragenscobertas', 'Garagem coberta')}
                {numero('i1_qtdgaragensdescobertas', 'Garagem descoberta')}
                {numero('i1_qtdbanheiros', 'Banheiros')}
                {numero('i1_qtdandares', 'Nº de andares')}
                {numero('i1_qtdelevadores', 'Elevadores')}
                {numero('i1_qtdambientes', 'Living/ambientes')}
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>Áreas</Card.Header>
            <Card.Body>
              <Row className="g-3">
                {numero('i1_construida', 'Construída', 2, 'any')}
                {numero('i1_util', 'Útil', 2, 'any')}
                {numero('i1_totalterreno', 'Total do terreno', 2, 'any')}
                {numero('i1_comum', 'Comum', 2, 'any')}
                {numero('i1_privativa', 'Privativa', 2, 'any')}
                {numero('i1_total', 'Total', 2, 'any')}
                {numero('i1_pedireito', 'Pé direito', 2, 'any')}
                {selecao('i1_medida', 'Medida', MEDIDAS, 2)}
                {texto('i1_dimensaoterreno', 'Medida do terreno', 2, { placeholder: 'ex: 10x25' })}
                {selecao('i1_face', 'Face', FACES, 2)}
                {selecao('i1_isolamento', 'Isolamento', ISOLAMENTOS, 2)}
                {selecao('i1_posicao', 'Posição', POSICOES, 2)}
                {numero('i1_distanciamar', 'Distância do mar', 2, 'any')}
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        {/* ───────── Internet e Anúncios ───────── */}
        <Tab eventKey="anuncios" title="Internet e Anúncios">
          <Card className="mb-3">
            <Card.Header>Internet e Anúncios</Card.Header>
            <Card.Body>
              <Row className="g-3">
                {check('i1_siteempresa', 'Site da empresa', 3)}
                {check('i1_oportunidadeneg', 'Oportunidade de negócio', 3)}
                {check('i1_destaque', 'Destaque', 3)}
                {check('i1_vlrsobconsul', 'Valor sob consulta', 3)}
                {check('i1_vlrnegoci', 'Valor negociável', 3)}
                {check('i1_vlrsitedarede', 'Site da rede', 3)}
                {check('i1_univentv', 'TV', 3)}
                {check('i1_portais', 'Divulgar em portais', 3)}
                {!desabilitado && podeUsarIa && (
                  <Col md={12}>
                    <Button variant="outline-primary" size="sm" onClick={() => setMostrarIa(true)}>
                      ✨ Usar IA para preencher título, descrição e palavras-chave
                    </Button>
                  </Col>
                )}
                <Col md={6}>
                  <Form.Group controlId="i1_seotitulo">
                    <Form.Label>Título do imóvel</Form.Label>
                    <Form.Control maxLength={50} value={v.i1_seotitulo ?? ''} disabled={desabilitado} onChange={(e) => setV('i1_seotitulo', e.target.value)} autoComplete="off" />
                    <Form.Text muted>{(v.i1_seotitulo || '').length}/50</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="i1_descricao">
                    <Form.Label>Descrição do imóvel</Form.Label>
                    <Form.Control as="textarea" rows={8} value={v.i1_descricao ?? ''} disabled={desabilitado} onChange={(e) => setV('i1_descricao', e.target.value)} />
                  </Form.Group>
                </Col>
                {texto('i1_seoplvrchave', 'Palavras-chave', 6, { maxLength: 100 })}
                <Col md={12}>
                  <Form.Group controlId="i1_seodescricao">
                    <Form.Label>Descrição SEO Google</Form.Label>
                    <Form.Control as="textarea" rows={3} maxLength={158} value={v.i1_seodescricao ?? ''} disabled={desabilitado} onChange={(e) => setV('i1_seodescricao', e.target.value)} />
                    <Form.Text muted>{(v.i1_seodescricao || '').length}/158</Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        {/* ───────── Confidencial e Captação ───────── */}
        <Tab eventKey="confidencial" title="Confidencial e Captação">
          <Card className="mb-3">
            <Card.Header>Confidencial</Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Label>Proprietário</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <span>{proprietario ? proprietario.nome : <span className="text-muted">não informado</span>}</span>
                    {podeEditar && (
                      <Button size="sm" variant="outline-primary" onClick={() => setEscolhendoProprietario(true)}>
                        {proprietario ? 'Trocar' : 'Escolher'}
                      </Button>
                    )}
                  </div>
                </Col>
                {texto('i1_cadprefeitura', 'Nº IPTU/INCRA', 4)}
                {texto('i1_cartorio', 'Cartório / Matrícula', 4, { placeholder: 'Ex: 2º / 82006' })}
                {texto('i1_eletrecidade', 'Eletricidade', 4)}
                {texto('i1_aguaesaneamento', 'Água / Saneamento', 4)}
                {check('i1_documentacao', 'Documentação pendente', 4)}
                {check('i1_autorizado', 'Autorizado para publicar', 4)}
                {check('i1_emparceria', 'Em parceria', 4)}
              </Row>
            </Card.Body>
          </Card>
          <Card className="mb-3">
            <Card.Header>Captação</Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Group controlId="captador">
                    <Form.Label>Captador</Form.Label>
                    <Form.Select value={captador ?? ''} disabled={desabilitado} onChange={(e) => setCaptador(e.target.value)}>
                      <option value="">(padrão: quem cadastra)</option>
                      {captadores.map((cap) => (
                        <option key={cap.public_id} value={cap.public_id}>
                          {cap.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <Card className="mb-3">
            <Card.Header>Outros documentos</Card.Header>
            <Card.Body>
              <DocumentosImovel publicId={imovel?.public_id} podeEditar={podeEditar} />
            </Card.Body>
          </Card>
        </Tab>

        {/* ───────── Fotos e Vídeo ───────── */}
        <Tab eventKey="fotos" title="Fotos e Vídeo">
          <Card className="mb-3">
            <Card.Header>Fotos do imóvel</Card.Header>
            <Card.Body>
              <GaleriaImovel publicId={imovel?.public_id} fotos={fotos} aoAtualizarFotos={setFotos} pendentes={pendentes} setPendentes={setPendentes} podeEditar={podeEditar} />
            </Card.Body>
          </Card>
          <Card className="mb-3">
            <Card.Header>Vídeo do imóvel no YouTube</Card.Header>
            <Card.Body>
              <Row className="g-3">{texto('i1_youtube', 'URL do vídeo', 6)}</Row>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {faltantes.length > 0 && (
        <Alert variant="danger">
          <strong>Cadastro incompleto.</strong> Para que o imóvel fique disponível (portais, feeds e envios), preencha:
          <ul className="mb-0">
            {faltantes.map(([campo, rotulo]) => (
              <li key={campo}>{rotulo}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* registro discreto, no fim do formulário (vale para todas as seções), antes do Salvar */}
      {!ehNovo && imovel && textoDatasImovel(imovel) && <div className="text-muted small mt-4 mb-4">{textoDatasImovel(imovel)}</div>}

      {podeEditar && (
        <>
          <Button type="submit" variant="success" className="botao-salvar ms-0" disabled={salvando}>
            {salvando ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Aguarde, salvando...
              </>
            ) : (
              'Salvar'
            )}
          </Button>
          {/* salvar flutuante (como no painel antigo: redondo, âmbar, ícone de disquete branco) */}
          <button type="submit" disabled={salvando} aria-label="Salvar" title="Salvar" className="botao-salvar-flutuante">
            <i className="bi bi-floppy-fill" />
          </button>
        </>
      )}

      <SeletorProprietario
        show={escolhendoProprietario}
        empresaId={empresaId}
        aoFechar={() => setEscolhendoProprietario(false)}
        aoEscolher={(cli) => {
          setProprietario(cli);
          setEscolhendoProprietario(false);
        }}
      />

      <GerarAnuncioIa
        show={mostrarIa}
        onHide={() => setMostrarIa(false)}
        dadosImovel={montarDadosParaIa()}
        existeConteudo={conteudoAnuncioJaExiste}
        onAceitar={aoAceitarIa}
      />
    </Form>
  );
}
